/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import * as types from './types';
import * as reducers from './reducers';
import { normalizeTextBoxAttachments } from '../../../utils/textBoxAttachment';

// Element updates enter through several reducers. Normalize only pages whose
// element arrays changed so attachments cannot outlive or drift from targets.
function normalizeChangedPageAttachments(state, nextState) {
  if (nextState === state) {
    return nextState;
  }

  const previousPagesById = new Map(state.pages.map((page) => [page.id, page]));
  let didNormalize = false;
  const pages = nextState.pages.map((page) => {
    const previousPage = previousPagesById.get(page.id);
    if (
      previousPage?.elements === page.elements ||
      !Array.isArray(page.elements)
    ) {
      return page;
    }

    const elements = normalizeTextBoxAttachments(page.elements);
    if (elements === page.elements) {
      return page;
    }

    didNormalize = true;
    return { ...page, elements };
  });

  return didNormalize ? { ...nextState, pages } : nextState;
}

/* eslint-disable complexity */
function reduceStoryState(state, { type, payload }) {
  switch (type) {
    case types.ADD_PAGE: {
      return reducers.addPage(state, payload);
    }

    case types.DELETE_PAGE: {
      return reducers.deletePage(state, payload);
    }

    case types.UPDATE_PAGE: {
      return reducers.updatePage(state, payload);
    }

    case types.UPDATE_SAVED_COLORS: {
      return reducers.updateSavedColors(state, payload);
    }

    case types.UPDATE_SAVED_COLOR_STATE: {
      return reducers.updateSavedColorState(state, payload);
    }

    case types.ARRANGE_PAGE: {
      return reducers.arrangePage(state, payload);
    }

    case types.REPLACE_CURRENT_PAGE: {
      return reducers.replaceCurrentPage(state, payload);
    }

    case types.SET_CURRENT_PAGE: {
      return reducers.setCurrentPage(state, payload);
    }

    case types.ADD_ELEMENTS: {
      return reducers.addElements(state, payload);
    }

    case types.DELETE_ELEMENTS: {
      return reducers.deleteElements(state, payload);
    }

    case types.DUPLICATE_ELEMENTS_BY_ID: {
      return reducers.duplicateElementsById(state, payload);
    }

    case types.UPDATE_ELEMENTS: {
      return reducers.updateElements(state, payload);
    }

    case types.SET_TEXT_BOX_ATTACHMENT: {
      return reducers.setTextBoxAttachment(state, payload);
    }

    case types.UPDATE_ELEMENTS_BY_RESOURCE_ID: {
      return reducers.updateElementsByResourceId(state, payload);
    }

    case types.DELETE_ELEMENTS_BY_RESOURCE_ID: {
      return reducers.deleteElementsByResourceId(state, payload);
    }

    case types.COMBINE_ELEMENTS: {
      return reducers.combineElements(state, payload);
    }

    case types.SET_BACKGROUND_ELEMENT: {
      return reducers.setBackgroundElement(state, payload);
    }

    case types.ARRANGE_ELEMENT: {
      return reducers.arrangeElement(state, payload);
    }

    case types.ARRANGE_ELEMENTS: {
      return reducers.arrangeElements(state, payload);
    }

    case types.SET_SELECTED_ELEMENTS: {
      return reducers.setSelectedElements(state, payload);
    }

    case types.SELECT_ELEMENT: {
      return reducers.selectElement(state, payload);
    }

    case types.UNSELECT_ELEMENT: {
      return reducers.unselectElement(state, payload);
    }

    case types.TOGGLE_ELEMENT_IN_SELECTION: {
      return reducers.toggleElement(state, payload);
    }

    case types.TOGGLE_LAYER: {
      return reducers.toggleLayer(state, payload);
    }

    case types.UPDATE_STORY: {
      return reducers.updateStory(state, payload);
    }

    case types.UPDATE_ANIMATION_STATE: {
      return reducers.updateAnimationState(state, payload);
    }

    case types.UPDATE_REMOTION_ANIMATIONS: {
      return reducers.updateRemotionAnimations(state, payload);
    }

    case types.ADD_ANIMATIONS: {
      return reducers.addAnimations(state, payload);
    }

    case types.RESTORE: {
      return reducers.restore(state, payload);
    }

    case types.ADD_GROUP: {
      return reducers.addGroup(state, payload);
    }

    case types.ARRANGE_GROUP: {
      return reducers.arrangeGroup(state, payload);
    }

    case types.UPDATE_GROUP: {
      return reducers.updateGroup(state, payload);
    }

    case types.DELETE_GROUP: {
      return reducers.deleteGroup(state, payload);
    }

    case types.DUPLICATE_GROUP: {
      return reducers.duplicateGroup(state, payload);
    }

    case types.REMOVE_ELEMENT_FROM_GROUP: {
      return reducers.removeElementFromGroup(state, payload);
    }

    default:
      return state;
  }
}
/* eslint-enable complexity */

function reducer(state, action) {
  return normalizeChangedPageAttachments(
    state,
    reduceStoryState(state, action)
  );
}

export default reducer;
